import React, { useState, useEffect } from 'react';
import { Box, Center, VStack } from '@chakra-ui/react';
import { useSpring, animated } from 'react-spring';
import { CARD_CONFIG } from '../../config/cardConfig';
import Card from './Card';
import VRFInfo from './VRFInfo';
import { useGameStore } from '../../stores/gameStore';
import { useGame } from '../../context/GameContext';

// VRF simulation using Web Crypto API
const generateVRFValue = async (seed) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(seed);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const normalizedValue = parseInt(hashArray.slice(0, 4).join(''), 10) / 0xffffffff;
    return normalizedValue;
};

const generateGameSeed = () => {
    const timestamp = Date.now();
    const randomBytes = new Uint8Array(16);
    crypto.getRandomValues(randomBytes);
    return `${timestamp}-${Array.from(randomBytes).join('')}`;
};

const CardGrid = ({ rows = 4, cols = 4 }) => {
    const [cards, setCards] = useState([]);
    const [flippedCards, setFlippedCards] = useState([]);
    const [disabledCards, setDisabledCards] = useState([]);
    const [gameSeed, setGameSeed] = useState('');
    const [vrfProofs, setVrfProofs] = useState([]);
    const [currentCardVRF, setCurrentCardVRF] = useState(null);
    const [currentFlipVRF, setCurrentFlipVRF] = useState(null);
    const [currentBonusMultiplier, setCurrentBonusMultiplier] = useState(null);
    const updateScore = useGameStore(state => state.updateScore);
    const { gameState, flipCard } = useGame();

    // Initialize cards with VRF
    useEffect(() => {
        const initializeCards = async () => {
            const totalCards = rows * cols;
            const newGameSeed = generateGameSeed();
            setGameSeed(newGameSeed);
            
            const proofs = [];
            const cardTypes = [];
            
            // Generate VRF values for each card
            for (let i = 0; i < totalCards; i++) {
                const cardSeed = `${newGameSeed}:card:${i}`;
                const vrfValue = await generateVRFValue(cardSeed);
                proofs.push(vrfValue);
                
                // Determine card type based on VRF value
                // This ensures fair distribution of card types
                let cardType;
                if (vrfValue < 0.4) {
                    cardType = CARD_CONFIG.TYPES.NORMAL;
                } else if (vrfValue < 0.7) {
                    cardType = CARD_CONFIG.TYPES.RARE;
                } else if (vrfValue < 0.9) {
                    cardType = CARD_CONFIG.TYPES.EPIC;
                } else {
                    cardType = CARD_CONFIG.TYPES.LEGENDARY;
                }
                cardTypes.push(cardType);
            }
            
            setVrfProofs(proofs);
            
            // Create the initial cards array
            const initialCards = Array.from({ length: totalCards }, (_, index) => ({
                id: index,
                type: cardTypes[index],
                position: calculatePosition(index, cols),
                matched: false,
                vrfProof: proofs[index]
            }));
            
            setCards(initialCards);
            
            // Set current VRF values for display
            setCurrentCardVRF(proofs[0]);
            setCurrentFlipVRF(await generateVRFValue(`${newGameSeed}:flip:0`));
            setCurrentBonusMultiplier(Math.floor(await generateVRFValue(`${newGameSeed}:bonus`) * 10) + 1);
        };
        
        initializeCards();
    }, [rows, cols]);

    // Calculate position based on index and columns
    const calculatePosition = (index, columns) => {
        const row = Math.floor(index / columns);
        const col = index % columns;
        return { row, col };
    };

    // Handle card click
    const handleCardClick = async (id) => {
        // Don't allow flipping if already flipped or disabled
        if (flippedCards.includes(id) || disabledCards.includes(id)) {
            return;
        }

        // Generate VRF for this flip
        const flipSeed = `${gameSeed}:flip:${id}`;
        const flipVRF = await generateVRFValue(flipSeed);
        setCurrentFlipVRF(flipVRF);

        // Flip the card
        const newFlippedCards = [...flippedCards, id];
        setFlippedCards(newFlippedCards);

        // If we have 2 flipped cards, check for a match
        if (newFlippedCards.length === 2) {
            const [firstId, secondId] = newFlippedCards;
            const firstCard = cards.find(card => card.id === firstId);
            const secondCard = cards.find(card => card.id === secondId);

            // Check if the cards match
            if (firstCard.type === secondCard.type) {
                // Mark cards as matched
                const updatedCards = cards.map(card => 
                    card.id === firstId || card.id === secondId
                        ? { ...card, matched: true }
                        : card
                );
                setCards(updatedCards);
                
                // Calculate reward based on card type and VRF
                const baseReward = CARD_CONFIG.REWARDS[firstCard.type] || 10;
                const bonusSeed = `${gameSeed}:bonus:${firstId}:${secondId}`;
                const bonusVRF = await generateVRFValue(bonusSeed);
                const bonusMultiplier = Math.floor(bonusVRF * 5) + 1; // 1-5x multiplier
                setCurrentBonusMultiplier(bonusMultiplier);
                
                const reward = baseReward * bonusMultiplier;
                updateScore(reward);
                
                // Reset flipped cards
                setFlippedCards([]);
            } else {
                // If no match, disable cards temporarily
                setDisabledCards([...disabledCards, ...newFlippedCards]);
                
                // Reset flipped cards after a delay
                setTimeout(() => {
                    setFlippedCards([]);
                    setDisabledCards(disabledCards.filter(id => !newFlippedCards.includes(id)));
                }, 1000);
            }
        }
    };

    // Animation for the grid
    const gridAnimation = useSpring({
        from: { opacity: 0, transform: 'scale(0.9)' },
        to: { opacity: 1, transform: 'scale(1)' },
        config: { tension: 120, friction: 14 }
    });

    return (
        <VStack spacing={6} align="center" w="100%">
            {/* VRF Information */}
            <VRFInfo 
                gameSeed={gameSeed} 
                cardVRF={currentCardVRF} 
                flipVRF={currentFlipVRF} 
                bonusMultiplier={currentBonusMultiplier} 
            />
            
            {/* Card Grid */}
            <Center w="100%">
                <animated.div style={gridAnimation}>
                    <Box
                        display="grid"
                        gridTemplateColumns={`repeat(${cols}, 1fr)`}
                        gridTemplateRows={`repeat(${rows}, 1fr)`}
                        gap={2}
                        p={1}
                        bg="whiteAlpha.100"
                        borderRadius="lg"
                        boxShadow="lg"
                        w="100%"
                        maxW="1200px"
                    >
                        {cards.map(card => (
                            <Card
                                key={card.id}
                                id={card.id}
                                type={card.type}
                                isFlipped={flippedCards.includes(card.id)}
                                isMatched={card.matched}
                                isDisabled={disabledCards.includes(card.id)}
                                onClick={() => flipCard(card.id)}
                                vrfProof={card.vrfProof}
                            />
                        ))}
                    </Box>
                </animated.div>
            </Center>
        </VStack>
    );
};

export default CardGrid;
